import React from 'react';
import './FmFeedback.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export type FmSpinnerSize = 'sm' | 'md' | 'lg';

export interface FmSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: FmSpinnerSize;
  /** Optional caption under the spinner (AntD Spin `tip`). */
  tip?: React.ReactNode;
  /**
   * When provided, renders as an overlay wrapper: children stay mounted and
   * dimmed while `spinning` is true (AntD `<Spin spinning>{children}</Spin>`).
   * Omit to render a standalone centered spinner.
   */
  spinning?: boolean;
  children?: React.ReactNode;
}

const Ring: React.FC<{ size: FmSpinnerSize }> = ({ size }) => (
  <span className={cx('fm-spinner__ring', `fm-spinner__ring--${size}`)} aria-hidden="true" />
);

/** Loading indicator — the design-system replacement for AntD `Spin`. */
export const FmSpinner: React.FC<FmSpinnerProps> = ({
  size = 'md',
  tip,
  spinning,
  className,
  children,
  ...rest
}) => {
  // Overlay form: dim the wrapped content and float a centered ring.
  if (children !== undefined) {
    return (
      <div className={cx('fm-spin-wrap', className)} {...rest}>
        <div className={cx('fm-spin-wrap__content', spinning && 'fm-spin-wrap__content--blurred')}>
          {children}
        </div>
        {spinning && (
          <output className="fm-spin-wrap__overlay" aria-live="polite">
            <Ring size={size} />
            {tip ? <span className="fm-spinner__tip">{tip}</span> : null}
          </output>
        )}
      </div>
    );
  }
  // Standalone centered spinner.
  return (
    <div className={cx('fm-spinner', className)} aria-live="polite" {...rest}>
      <Ring size={size} />
      {tip ? <span className="fm-spinner__tip">{tip}</span> : null}
    </div>
  );
};

export interface FmEmptyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Primary line (AntD Empty `description`). */
  description?: React.ReactNode;
  /** Optional leading glyph/illustration above the text. */
  icon?: React.ReactNode;
  /** Optional call-to-action row below the text. */
  action?: React.ReactNode;
  children?: React.ReactNode;
}

/** Empty / no-data state — the design-system replacement for AntD `Empty`. */
export const FmEmpty: React.FC<FmEmptyProps> = ({
  description,
  icon,
  action,
  className,
  children,
  ...rest
}) => (
  <div className={cx('fm-empty', className)} {...rest}>
    {icon ? (
      <div className="fm-empty__icon" aria-hidden="true">
        {icon}
      </div>
    ) : null}
    {description ? <div className="fm-empty__text">{description}</div> : null}
    {children}
    {action ? <div className="fm-empty__action">{action}</div> : null}
  </div>
);

export type FmProgressStatus = 'normal' | 'success' | 'exception';

export interface FmProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Completion 0–100. */
  percent: number;
  size?: 'sm' | 'default';
  status?: FmProgressStatus;
  /** Show the trailing percent label (AntD `showInfo`). */
  showInfo?: boolean;
}

const PROGRESS_STATUS_CLASS: Record<FmProgressStatus, string | null> = {
  normal: null,
  success: 'fm-progress--success',
  exception: 'fm-progress--exception',
};

/** Linear progress bar — the design-system replacement for AntD `Progress`. */
export const FmProgress: React.FC<FmProgressProps> = ({
  percent,
  size = 'default',
  status = 'normal',
  showInfo = true,
  className,
  ...rest
}) => {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div
      className={cx(
        'fm-progress',
        size === 'sm' && 'fm-progress--sm',
        PROGRESS_STATUS_CLASS[status],
        className,
      )}
      {...rest}
    >
      <progress
        className="fm-progress__track"
        value={clamped}
        max={100}
        aria-label={`${Math.round(clamped)}%`}
      />
      {showInfo ? <span className="fm-progress__info">{Math.round(clamped)}%</span> : null}
    </div>
  );
};

export interface FmSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of placeholder lines (AntD Skeleton paragraph rows). */
  rows?: number;
  /** Render a wider title bar above the lines. */
  showTitle?: boolean;
}

/** Loading placeholder — the design-system replacement for AntD `Skeleton`. */
export const FmSkeleton: React.FC<FmSkeletonProps> = ({
  rows = 3,
  showTitle = true,
  className,
  ...rest
}) => {
  // Stable, non-positional keys — placeholder rows never reorder.
  const lineKeys = Array.from({ length: rows }, (_, i) => `fm-skeleton-line-${i}`);
  return (
    <div className={cx('fm-skeleton', className)} aria-hidden="true" {...rest}>
      {showTitle ? <span className="fm-skeleton__line fm-skeleton__line--title" /> : null}
      {lineKeys.map((key, i) => (
        <span
          key={key}
          className={cx('fm-skeleton__line', i === rows - 1 && 'fm-skeleton__line--last')}
        />
      ))}
    </div>
  );
};
